import { cn } from '@/shared/lib/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'teal' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';
/**
 * `pill` is the mockup's full-radius action button; `rounded` is the 12px
 * radius used by compact in-content buttons. Kept as a prop rather than
 * changing the default, because the copy/retry buttons scattered through the
 * flows are 12px in the reference too.
 */
export type ButtonShape = 'rounded' | 'pill';

const BASE =
  'relative inline-flex items-center justify-center gap-2 font-semibold ' +
  'transition-[background-color,color,box-shadow,opacity] duration-150 ' +
  'select-none outline-none focus-visible:outline-2 focus-visible:outline-offset-2 ' +
  'focus-visible:outline-[var(--focus-ring)] ' +
  'disabled:cursor-not-allowed';

const VARIANTS: Record<ButtonVariant, string> = {
  /*
   * --on-brand, not `text-white`: the dark theme's brand colour is light enough
   * that white text on it fails AA. See the token definition.
   *
   * The disabled state is a flat GREY FILL, matching Withdraw_to_Card_1 and
   * Transfer_1 where the un-submittable primary is grey. A dimmed blue (the old
   * `opacity-50`) still reads as "blue button", and users tap blue buttons.
   */
  primary:
    'bg-[var(--brand)] text-[var(--on-brand)] shadow-sm ' +
    'enabled:hover:bg-[var(--brand-strong)] enabled:active:bg-[var(--brand-strong)] ' +
    'disabled:bg-[var(--text-disabled)] disabled:text-white disabled:shadow-none',
  // The mockup's second action (Top_Up_2/3 "Back Home"): the teal gradient stop
  // as a flat fill. Ink is --brand-deep, not white: white on #3bd4d4 is 1.8:1.
  teal: 'bg-[var(--brand-teal)] text-[var(--brand-deep)] enabled:hover:brightness-95 disabled:opacity-50',
  secondary:
    'bg-[var(--brand-subtle)] text-[var(--brand)] enabled:hover:bg-[var(--surface-hover)] disabled:opacity-50',
  ghost:
    'bg-transparent text-[var(--text-primary)] enabled:hover:bg-[var(--surface-hover)] disabled:opacity-50',
  danger:
    'bg-[var(--danger)] text-white enabled:hover:brightness-95 enabled:active:brightness-90 disabled:opacity-50'
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-body-sm',
  md: 'h-11 px-4 text-body',
  lg: 'h-13 px-6 text-body'
};

const SHAPES: Record<ButtonShape, string> = {
  rounded: 'rounded-md',
  pill: 'rounded-pill'
};

/**
 * The button's visual recipe, without the `<button>`.
 *
 * For the one case a `<button>` cannot serve: a full-width action that is really
 * NAVIGATION. Wrapping a `<Button>` in a `<Link>` nests interactive content
 * inside an anchor — invalid HTML, two tab stops, and a screen reader announcing
 * "link, button". A `<Link className={buttonClasses(...)}>` is one control that
 * middle-clicks, opens in a new tab and reads as the link it is.
 */
export function buttonClasses(
  options: {
    variant?: ButtonVariant;
    size?: ButtonSize;
    shape?: ButtonShape;
    fullWidth?: boolean;
    className?: string;
  } = {}
): string {
  const {
    variant = 'primary',
    size = 'md',
    shape = 'rounded',
    fullWidth = false,
    className
  } = options;
  return cn(BASE, VARIANTS[variant], SIZES[size], SHAPES[shape], fullWidth && 'w-full', className);
}
